<template>
  <div
    class="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-8"
  >
    <!-- Header Section -->
    <div class="mb-8">
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <!-- Small Heading (Left) -->
        <div class="flex items-center gap-2">
          <BarChart3 class="w-4 h-4 text-indigo-600" />
          <h2
            class="text-xs sm:text-sm font-semibold text-slate-600 uppercase tracking-wider"
          >
            Attendance Analytics
          </h2>
        </div>

        <!-- Filters (Right) -->
        <div
          class="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto"
        >
          <!-- Date Range Dropdown -->
          <div class="relative group w-full sm:w-52">
            <select
              v-model="dateRangeOption"
              @change="handleDateRangeChange"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
            >
              <option
                v-for="option in dateRangeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <!-- Year Input -->
          <!-- Year Input (Only Full Year & Custom Range) -->
          <div
            v-if="dateRangeOption === 'full' || dateRangeOption === 'custom'"
            class="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-slate-200 shadow-sm w-full sm:w-auto"
          >
            <label class="text-xs font-medium text-slate-600 whitespace-nowrap">
              Year:
            </label>
            <input
              v-model.number="selectedYearLocal"
              type="number"
              class="w-full sm:w-24 px-2 py-1 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      <!-- Custom Date Range -->
      <div
        v-if="dateRangeOption === 'custom'"
        class="flex flex-col sm:flex-row gap-3 mt-4"
      >
        <input
          v-model="startDate"
          type="date"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
        />
        <input
          v-model="endDate"
          type="date"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
        />
      </div>
    </div>

    <!-- Summary Cards Grid -->
    <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
      <!-- Present -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Present Days
          </p>
          <CheckCircle2 class="w-5 h-5 text-emerald-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.presentDays ?? 0 }}
        </p>
      </div>

      <!-- Absent -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Absent Days
          </p>
          <XCircle class="w-5 h-5 text-red-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.absentDays ?? 0 }}
        </p>
      </div>

      <!-- Late -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Late Days
          </p>
          <Clock class="w-5 h-5 text-amber-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.lateDays ?? 0 }}
        </p>
      </div>

      <!-- Total Hours -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Total Hours
          </p>
          <Briefcase class="w-5 h-5 text-indigo-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.totalWorkingHours ?? 0 }}
        </p>
      </div>

      <!-- Avg Check-In -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Avg Check-In
          </p>
          <LogIn class="w-5 h-5 text-blue-500" />
        </div>
        <p class="text-xl font-bold text-gray-900">
          {{ attendanceData?.summary?.averageCheckIn ?? "—" }}
        </p>
      </div>

      <!-- Avg Check-Out -->
      <div
        class="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200"
      >
        <div class="flex items-center justify-between mb-2">
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Avg Check-Out
          </p>
          <LogOut class="w-5 h-5 text-purple-500" />
        </div>
        <p class="text-xl font-bold text-gray-900">
          {{ attendanceData?.summary?.averageCheckOut ?? "—" }}
        </p>
      </div>
    </div>

    <!-- Charts Section -->
    <div
      v-if="attendanceData?.monthlyStats"
      class="space-y-8"
    >
      <!-- Line Chart -->
      <div class="bg-white rounded-2xl p-6 border">
        <h3 class="text-lg font-bold text-slate-900 mb-6">
          Monthly Attendance Trend
        </h3>
        <div class="relative h-80 sm:h-96 w-full">
          <canvas
            ref="lineChart"
            class="w-full !max-w-none"
          ></canvas>
        </div>
      </div>

      <!-- Bar Chart -->
      <div class="bg-white rounded-2xl p-6 border">
        <h3 class="text-lg font-bold text-slate-900 mb-6">
          Monthly Working Hours
        </h3>
        <div class="relative h-80 sm:h-96 w-full">
          <canvas
            ref="barChart"
            class="w-full !max-w-none"
          ></canvas>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12"
    >
      <BarChart3 class="w-12 h-12 text-slate-300 mb-4" />
      <p class="text-slate-500 text-lg font-medium">
        No attendance data available for display.
      </p>
    </div>
  </div>
</template>
<script setup>
  import { ref, watch, nextTick, onMounted, onUnmounted, computed } from "vue";
  import { Chart, registerables } from "chart.js/auto";
  import {
    CheckCircle2,
    XCircle,
    Clock,
    Briefcase,
    LogIn,
    LogOut,
    BarChart3,
  } from "lucide-vue-next";

  Chart.register(...registerables);

  const props = defineProps({
    userId: String,
    userName: String,
    selectedYear: Number,
    attendanceData: Object,
  });

  const emit = defineEmits(["update:selectedYear", "filter-change"]);

  const lineChart = ref(null);
  const barChart = ref(null);

  let lineChartInstance = null;
  let barChartInstance = null;

  const selectedYearLocal = ref(props.selectedYear || new Date().getFullYear());

  const dateRangeOption = ref("full");
  const startDate = ref("");
  const endDate = ref("");

  const currentYear = new Date().getFullYear();

  const isCurrentYear = computed(() => {
    return selectedYearLocal.value === currentYear;
  });

  const dateRangeOptions = computed(() => {
    if (isCurrentYear.value) {
      return [
        { label: "Full Year", value: "full" },
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last 7 Days", value: "last7days" },
        { label: "Last 30 Days", value: "last30days" },
        { label: "Custom Range", value: "custom" },
      ];
    }

    return [
      { label: "Full Year", value: "full" },
      { label: "Custom Range", value: "custom" },
    ];
  });

  watch(
    () => props.selectedYear,
    (val) => {
      selectedYearLocal.value = val;
    },
  );

  watch(selectedYearLocal, (val) => {
    emit("update:selectedYear", val);
    emitFilters();
  });

  watch([startDate, endDate], ([s, e]) => {
    if (dateRangeOption.value === "custom" && s && e) {
      emitFilters();
    }
  });

  function handleDateRangeChange() {
    const today = new Date();
    const format = (d) => d.toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today":
        startDate.value = format(today);
        endDate.value = format(today);
        break;

      case "yesterday":
        // eslint-disable-next-line no-case-declarations
        const y = new Date();
        y.setDate(today.getDate() - 1);
        startDate.value = format(y);
        endDate.value = format(y);
        break;

      case "last7days":
        // eslint-disable-next-line no-case-declarations
        const past7 = new Date();
        past7.setDate(today.getDate() - 6);
        startDate.value = format(past7);
        endDate.value = format(today);
        break;

      case "last30days":
        // eslint-disable-next-line no-case-declarations
        const past30 = new Date();
        past30.setDate(today.getDate() - 29);
        startDate.value = format(past30);
        endDate.value = format(today);
        break;

      default:
        startDate.value = "";
        endDate.value = "";
    }

    emitFilters();
  }

  function emitFilters() {
    const formatToMonthDay = (date) => {
      if (!date) return null;

      const d = new Date(date);

      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      return `${month}-${day}`; // MM-DD format
    };

    const payload = {
      year: selectedYearLocal.value,
      startDate: formatToMonthDay(startDate.value),
      endDate: formatToMonthDay(endDate.value),
    };

    emit("filter-change", payload);
  }

  watch(
    () => props.attendanceData,
    async (data) => {
      if (!data) return;

      await nextTick();

      if (lineChartInstance) lineChartInstance.destroy();
      if (barChartInstance) barChartInstance.destroy();

      updateCharts(data);
    },
  );

  function updateCharts(data) {
    const { monthlyStats = {}, dailyStats = {} } = data;

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let labels = [];
    let presentData = [];
    let absentData = [];
    let lateData = [];
    let workingMinutesData = [];

    if (dateRangeOption.value === "full") {
      labels = months;

      presentData = months.map((m) => monthlyStats[m]?.present ?? 0);
      absentData = months.map((m) => monthlyStats[m]?.absent ?? 0);
      lateData = months.map((m) => monthlyStats[m]?.late ?? 0);
      workingMinutesData = months.map(
        (m) => monthlyStats[m]?.workingMinutes ?? 0,
      );
    } else {
      const dates = [];
      const start = new Date(startDate.value);
      const end = new Date(endDate.value);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = new Date(d).toISOString().split("T")[0];
        dates.push(iso);
      }

      labels = dates.map((d) =>
        new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      );

      presentData = dates.map((d) => dailyStats[d]?.present ?? 0);
      absentData = dates.map((d) => dailyStats[d]?.absent ?? 0);
      lateData = dates.map((d) => dailyStats[d]?.late ?? 0);
      workingMinutesData = dates.map((d) => dailyStats[d]?.workingMinutes ?? 0);
    }

    if (lineChart.value) {
      const ctx = lineChart.value.getContext("2d");

      lineChartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Present",
              data: presentData,
              backgroundColor: "#10b981",
              stack: "attendance",
            },
            {
              label: "Absent",
              data: absentData,
              backgroundColor: "#ef4444",
              stack: "attendance",
            },
            {
              label: "Late",
              data: lateData,
              backgroundColor: "#f59e0b",
              stack: "attendance",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true },
          },
        },
      });
    }

    if (barChart.value) {
      const ctx = barChart.value.getContext("2d");

      barChartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Working Hours",
              data: workingMinutesData,
              backgroundColor: "#4f46e5",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label(context) {
                  const minutes = context.raw;
                  const h = Math.floor(minutes / 60);
                  const m = minutes % 60;

                  return `Working Time: ${h}h ${m}m`;
                },
              },
            },
          },
          scales: {
            y: {
              ticks: {
                callback(value) {
                  const h = Math.floor(value / 60);
                  const m = value % 60;

                  return `${h}h ${m}m`;
                },
              },
            },
          },
        },
      });
    }
  }

  onMounted(() => {
    if (props.attendanceData) {
      updateCharts(props.attendanceData);
    }
  });

  onUnmounted(() => {
    if (lineChartInstance) lineChartInstance.destroy();
    if (barChartInstance) barChartInstance.destroy();
  });
</script>
<style scoped>
  /* Custom chart container styling */
  canvas {
    @apply rounded-lg;
  }
</style>
